import React from 'react';
import PropTypes from 'prop-types';

import Img from '../../../Img';
import useStartPowerCheckout from '../../../hooks/useStartPowerCheckout';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import { pluralize } from '../../../../utils/formatting';
import { SavedListsAccessStatus, POWER_CTA_LABEL, RESUME_POWER_CTA_LABEL } from '../constants';
import styles from './savedListsLapsedMessage.scss';

const LOCK_ICON_SRC = '/img/v2/icons/icon-feature-lock.svg';

const LOCK_ICON_ALT = 'Saved Lists locked';

const SavedListsLapsedMessage = ({
    'data-testid': dataTestId,
    status,
    totalPlayerCount,
    totalListCount,
    hub,
    onResumeSuccess,
}) => {
    const { startPowerCheckout } = useStartPowerCheckout();
    const isPastDue = status === SavedListsAccessStatus.PAST_DUE;
    const ctaLabel = isPastDue ? RESUME_POWER_CTA_LABEL : POWER_CTA_LABEL;

    // The CTA label switches on the server status per the ticket (RESUME POWER for PastDue, GET
    // POWER for Lapsed). One startPowerCheckout call passes surface + gatedFeature + confirmResume;
    // the hook then branches on Redux auth.user.hasPastDueSubscription — past-due opens the
    // resume-confirm popup, which resumes in place via the shared Update-CC modal (TMS-2338); the
    // deferred onResumeSuccess refreshes this tab once Power is restored, so it flips to the active
    // saved-lists view. Non-past-due Lapsed enters the upgrade funnel with attribution.
    const handleCta = () => {
        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.SAVED_PLAYER_LIST,
            confirmResume: isPastDue,
            onResumeSuccess,
        });
    };

    // Loss-aversion counts read straight from the feed; skip the line when either count is missing
    // (default/zero or a failed refetch) so it never renders a self-contradictory "N across 0 lists".
    const hasCounts = totalPlayerCount > 0 && totalListCount > 0;
    const countsText = `You have ${totalPlayerCount} saved ${pluralize('player', totalPlayerCount)} ` +
        `across ${totalListCount} ${pluralize('list', totalListCount)} — renew Power to see them again.`;

    return (
        <div className={styles.wrapper} data-testid={dataTestId}>
            {hub && (
                <div
                    className={styles.dimmedHub}
                    aria-hidden="true"
                    inert=""
                    data-testid={dataTestId && `${dataTestId}.dimmed-hub`}
                >
                    {hub}
                </div>
            )}
            <div className={styles.lockbox} data-testid={dataTestId && `${dataTestId}.lockbox`}>
                <Img
                    src={LOCK_ICON_SRC}
                    alt={LOCK_ICON_ALT}
                    className={styles.lockIcon}
                    data-testid={dataTestId && `${dataTestId}.lock-icon`}
                />
                {hasCounts && (
                    <p className={styles.counts} data-testid={dataTestId && `${dataTestId}.counts`}>
                        {countsText}
                    </p>
                )}
                <button
                    type="button"
                    className={styles.cta}
                    onClick={handleCta}
                    data-testid={dataTestId && `${dataTestId}.cta`}
                >
                    {ctaLabel}
                </button>
            </div>
        </div>
    );
};

SavedListsLapsedMessage.propTypes = {
    'data-testid': PropTypes.string,
    status: PropTypes.oneOf([
        SavedListsAccessStatus.LAPSED,
        SavedListsAccessStatus.PAST_DUE,
    ]).isRequired,
    totalPlayerCount: PropTypes.number,
    totalListCount: PropTypes.number,
    hub: PropTypes.node,
    onResumeSuccess: PropTypes.func,
};

SavedListsLapsedMessage.defaultProps = {
    'data-testid': null,
    totalPlayerCount: 0,
    totalListCount: 0,
    hub: null,
    onResumeSuccess: null,
};

export default SavedListsLapsedMessage;
